import type React from 'react';
import './MessageContent.css';

interface MessageContentProps {
  content: string;
  onQuestionClick?: (question: string) => void;
  isAssistant: boolean;
  /** Optional inline element to show at the end of the main text (like token usage icon) */
  inlineSuffix?: React.ReactNode;
  /** Explicit companion name to display at the top of assistant replies */
  companionName?: string;
  /** When true, follow-up buttons are disabled (e.g. while a message is loading) */
  disabled?: boolean;
}

export const MessageContent: React.FC<MessageContentProps> = ({
  content,
  onQuestionClick,
  isAssistant,
  inlineSuffix,
  companionName,
  disabled,
}) => {
  if (!isAssistant) {
    return (
      <p className="message-text">
        {content}
        {inlineSuffix}
      </p>
    );
  }

  // Split content into lines to analyze
  const lines = content.split('\n');
  const followUpQuestions: { display: string; action: string }[] = [];

  let suggestionHeadingFound = false;
  let splitIndex = lines.length;

  // Pattern for "1. Question?" - handle various formats including 1) or *
  const numberedListPattern = /^(\d+[.)]|\*|-)\s+(.+)$/;

  // Suggestion markers to look for (helps identify the break point)
  const suggestionMarkers = [
    'things you might wish to ask',
    'you might want to ask',
    'sormak isteyebileceğin birkaç şey',
    'bana şunları sorabilirsin',
    'ask me about',
    'ask about',
    'here are some',
    'you may also',
    'you might also',
    'perhaps you would',
    'belki de',
    'i could also',
    'ben de sana',
    'would you like to',
    'shall i',
    'şunları da merak',
    "if you'd like",
  ];

  /**
   * Check whether a line looks like a pipe-separated follow-up suggestion.
   * Valid: "Avatar perspective text | User perspective text"
   * Requires both sides to be substantive (>10 chars) to avoid false positives
   * on normal prose that happens to contain a pipe character.
   */
  const isPipeSuggestionLine = (line: string): boolean => {
    if (!line.includes('|')) return false;
    const parts = line.split('|').map((s) => s.trim());
    // Require exactly 2 parts, both substantive
    if (parts.length !== 2) return false;
    if (parts[0].length < 10 || parts[1].length < 10) return false;
    return true;
  };

  /**
   * Extract display/action from a raw text line that may or may not have a
   * numbered prefix and may or may not have a pipe separator.
   */
  const extractQuestion = (rawLine: string): { display: string; action: string } | null => {
    let text = rawLine;

    // Strip numbered/bulleted prefix if present
    const prefixMatch = text.match(numberedListPattern);
    if (prefixMatch) {
      text = prefixMatch[2];
    }

    text = text.trim();
    if (!text) return null;

    if (text.includes('|')) {
      const parts = text.split('|').map((s) => s.trim());
      if (parts.length >= 2 && parts[0].length > 5 && parts[1].length > 5) {
        return { display: parts[0], action: parts[1] };
      }
    }

    // No pipe — use the text as both display and action
    if (text.length > 10) {
      return { display: text, action: text };
    }

    return null;
  };

  // ── Pass 1: Try to find numbered list items at the bottom ──
  let inQuestionList = false;

  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i].trim();
    if (!line && !inQuestionList) continue;

    const match = line.match(numberedListPattern);

    if (match) {
      const q = extractQuestion(line);
      if (q) {
        followUpQuestions.unshift(q);
        inQuestionList = true;
        splitIndex = i;
      }
    } else if (inQuestionList && line === '') {
      // Empty line within or just above the list
      splitIndex = i;
    } else if (
      inQuestionList &&
      suggestionMarkers.some((marker) => line.toLowerCase().includes(marker))
    ) {
      // Found the heading line
      suggestionHeadingFound = true;
      splitIndex = i;
      break;
    } else {
      if (inQuestionList) break;
    }
  }

  // ── Pass 2: If no numbered items found, scan for ALL trailing pipe-separated lines ──
  if (followUpQuestions.length === 0) {
    // Walk backwards, collecting contiguous pipe-suggestion lines (allowing
    // empty lines and suggestion-heading lines in between).
    let foundAny = false;

    for (let i = lines.length - 1; i >= 0; i--) {
      const line = lines[i].trim();

      // Skip trailing blank lines
      if (!line && !foundAny) continue;

      if (isPipeSuggestionLine(line)) {
        const q = extractQuestion(line);
        if (q) {
          followUpQuestions.unshift(q);
          foundAny = true;
          splitIndex = i;
        }
        continue;
      }

      // Allow blank lines between suggestion lines
      if (foundAny && line === '') {
        splitIndex = i;
        continue;
      }

      // Check for a suggestion heading right above
      if (foundAny && suggestionMarkers.some((marker) => line.toLowerCase().includes(marker))) {
        suggestionHeadingFound = true;
        splitIndex = i;
        break;
      }

      // Anything else means we've left the suggestion zone
      break;
    }
  }

  // Reconstruct main content without the questions part
  const mainContentLines = lines.slice(0, splitIndex);
  // Remove trailing empty lines from main content
  while (
    mainContentLines.length > 0 &&
    mainContentLines[mainContentLines.length - 1].trim() === ''
  ) {
    mainContentLines.pop();
  }
  const mainContent = mainContentLines.join('\n').trim();
  const headingLine = suggestionHeadingFound ? lines[splitIndex].trim() : null;

  return (
    <div className="message-content-root">
      {isAssistant && companionName && (
        <div className="message-companion-name">{companionName}</div>
      )}

      {mainContent && (
        <p className="message-text">
          {mainContent}
          {inlineSuffix}
        </p>
      )}

      {!mainContent && inlineSuffix && <div className="inline-suffix-only">{inlineSuffix}</div>}

      {followUpQuestions.length > 0 && (
        <div className="follow-up-section">
          {headingLine && <div className="follow-up-heading">{headingLine}</div>}
          <div className="questions-stack">
            {followUpQuestions.map((q) => (
              <button
                key={q.display}
                type="button"
                className="follow-up-btn"
                onClick={() => onQuestionClick?.(q.action)}
                title={q.display}
                disabled={disabled}
              >
                <div className="follow-up-icon">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
                <span className="follow-up-text">{q.display}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageContent;
